/**
 * Date preset utilities for CRM filters
 * Handles calculation of preset date ranges (Today, This Week, This Month)
 */

import { i18n } from "@/plugins/i18n";
import BusinessDateFormats from "@/modules/common/filters/BusinessDateFormats";
import { formatToISODate, getStartOfWeek } from "@/modules/common/utils/dateUtils";

let startDayOfWeek = 0;

export function setStartDayOfWeek(firstDayOfWeek) {
  startDayOfWeek = firstDayOfWeek;
}

export const PRESET_TYPES = {
  TODAY: "today",
  THIS_WEEK: "this_week",
  THIS_MONTH: "this_month",
  NEXT_WEEK: "next_week",
  NEXT_MONTH: "next_month",
};

/**
 * Gets the date range for a given preset value object
 * @param {Object} filterValue - Filter value object with { dynamic: string, ignore_year: boolean }
 * @returns {Object|null} - { range: { gte: 'YYYY-MM-DD', lte: 'YYYY-MM-DD' }, ignore_year: boolean } or null if invalid
 */
export function getFilterValue(filterValue = {}) {
  const presetType = filterValue.dynamic;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  let range;

  switch (presetType) {
    case PRESET_TYPES.TODAY: {
      const todayStr = formatToISODate(today);
      range = { gte: todayStr, lte: todayStr };
      break;
    }

    case PRESET_TYPES.THIS_WEEK: {
      const startOfWeekDate = getStartOfWeek(startDayOfWeek);
      const endOfWeek = new Date(startOfWeekDate);
      endOfWeek.setDate(startOfWeekDate.getDate() + 6);
      range = { gte: formatToISODate(startOfWeekDate), lte: formatToISODate(endOfWeek) };
      break;
    }

    case PRESET_TYPES.THIS_MONTH: {
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      range = { gte: formatToISODate(startOfMonth), lte: formatToISODate(endOfMonth) };
      break;
    }

    case PRESET_TYPES.NEXT_WEEK: {
      const startOfNextWeek = getStartOfWeek(startDayOfWeek);
      startOfNextWeek.setDate(startOfNextWeek.getDate() + 7);
      const endOfNextWeek = new Date(startOfNextWeek);
      endOfNextWeek.setDate(startOfNextWeek.getDate() + 6);
      range = { gte: formatToISODate(startOfNextWeek), lte: formatToISODate(endOfNextWeek) };
      break;
    }

    case PRESET_TYPES.NEXT_MONTH: {
      const startOfNextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
      const endOfNextMonth = new Date(today.getFullYear(), today.getMonth() + 2, 0);
      range = { gte: formatToISODate(startOfNextMonth), lte: formatToISODate(endOfNextMonth) };
      break;
    }

    default:
      return null;
  }

  const ignoreYear = filterValue.ignore_year ?? true;
  return { range, ignore_year: ignoreYear };
}

/**
 * Gets the display label for a preset type
 * @param {string} presetType - One of PRESET_TYPES values
 * @returns {string} - Translated label
 */
export function getPresetLabel(presetType) {
  const labels = {
    [PRESET_TYPES.TODAY]: i18n.t("client.crm_page.filters.date_presets.today"),
    [PRESET_TYPES.THIS_WEEK]: i18n.t("client.crm_page.filters.date_presets.this_week"),
    [PRESET_TYPES.THIS_MONTH]: i18n.t("client.crm_page.filters.date_presets.this_month"),
    [PRESET_TYPES.NEXT_WEEK]: i18n.t("client.crm_page.filters.date_presets.next_week"),
    [PRESET_TYPES.NEXT_MONTH]: i18n.t("client.crm_page.filters.date_presets.next_month"),
  };
  return labels[presetType] || "";
}

/**
 * Gets the display label for a preset type with the value
 * @param {string} presetType - One of PRESET_TYPES values
 * @returns {string} - Translated label with the value
 */
function getDateNoYear(date, ignoreYear) {
  const dateStr = BusinessDateFormats.dayShortMonthYear(date);
  if (ignoreYear) {
    return dateStr.split(",")[0];
  }

  return dateStr;
}
export function getPresetLabelWithValue(filterValue = {}) {
  const label = getPresetLabel(filterValue.dynamic);
  const value = getFilterValue(filterValue);

  if (!label || !value) {
    return null;
  }

  const {
    range: { gte, lte },
  } = value;
  const startDate = getDateNoYear(gte, filterValue.ignore_year);
  const endDate = getDateNoYear(lte, filterValue.ignore_year);

  if (gte === lte) {
    return `${label} ${startDate}`;
  }

  return `${label} ${startDate} - ${endDate}`;
}

/**
 * Gets all preset options for dropdown (Today, This Week, This Month)
 * @returns {Array} - Array of { text: string, value: string } objects for VcSelectField
 */
export function getPresetOptions() {
  const presets = [
    PRESET_TYPES.TODAY,
    PRESET_TYPES.THIS_WEEK,
    PRESET_TYPES.THIS_MONTH,
    PRESET_TYPES.NEXT_WEEK,
    PRESET_TYPES.NEXT_MONTH,
  ];
  return presets.map((preset) => {
    const label = getPresetLabel(preset);
    return {
      text: label,
      value: preset,
    };
  });
}

/**
 * Gets the filter value for a given month (1-12)
 * @param {number} month - Month number (1-12)
 * @returns {Object|null} - { month: number } or null if invalid
 */
export function getMonthFilterValue(month) {
  if (month < 1 || month > 12) {
    return null;
  }

  return { month };
}

/**
 * Gets the display label for a month (1-12) using BusinessDateFormats
 * @param {number} month - Month number (1-12)
 * @returns {string} - Month name
 */
export function getMonthLabel(month) {
  return BusinessDateFormats.monthName(month);
}

/**
 * Gets all month options for dropdown (1-12)
 * @returns {Array} - Array of { text: string, value: number } objects for VcSelectField
 */
export function getMonthOptions() {
  return Array.from({ length: 12 }, (_, i) => {
    const month = i + 1;
    const label = getMonthLabel(month);
    return {
      text: label,
      value: month,
    };
  });
}
